import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { t } from '../../lib/i18n';

const inputClass = 'bg-black/20 border-white/10 text-white placeholder:text-white/30 h-10 focus-visible:ring-[var(--lux-gold)]';

/**
 * Email/password login + register form. Submits {isLogin, email, password, name} to onSubmit.
 * Mode can be controlled by the parent (isLogin + onModeChange) or left uncontrolled.
 */
export const LoginRegisterForm = ({ language, loading, onSubmit, isLogin: controlledMode, onModeChange }) => {
  const [internalMode, setInternalMode] = useState(true);
  const isLogin = typeof controlledMode === 'boolean' ? controlledMode : internalMode;
  const setIsLogin = (next) => {
    setInternalMode(next);
    if (onModeChange) onModeChange(next);
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ isLogin, email, password, name });
  };

  return (
    <Card className="bg-lux-panel/90 border-white/[0.08] shadow-[0_12px_32px_rgba(0,0,0,0.55)]" data-testid="auth-form-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-xl text-white">
          {isLogin ? t('loginTitle', language) : t('registerTitle', language)}
        </CardTitle>
        <CardDescription className="text-white/40 text-sm">
          {language === 'fr' ? 'Sauvegardez votre progression' : 'Save your progress'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          {!isLogin && (
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">{t('name', language)}</Label>
              <Input
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                placeholder={t('name', language)}
                required={!isLogin}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-white/60 text-xs">{t('email', language)}</Label>
            <Input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="email@example.com"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-white/60 text-xs">{t('password', language)}</Label>
            <div className="relative">
              <Input
                data-testid="login-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputClass} pr-10`}
                placeholder="••••••••"
                required
                minLength={6}
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            data-testid={isLogin ? 'login-submit-button' : 'register-submit-button'}
            type="submit"
            disabled={loading}
            className="w-full bg-white/10 border border-white/10 text-white hover:bg-white/15 font-semibold h-10 active:scale-[0.98] transition-[background-color,transform] duration-200"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="flex items-center gap-2">
                {isLogin ? t('login', language) : t('register', language)}
                <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-white/30 mt-4">
          {isLogin ? t('noAccount', language) : t('hasAccount', language)}{' '}
          <button
            data-testid="auth-toggle-button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-[var(--lux-gold)] hover:text-[var(--lux-amber)] transition-colors duration-200"
          >
            {isLogin ? t('register', language) : t('login', language)}
          </button>
        </p>
      </CardContent>
    </Card>
  );
};
